/** API key panel: add, label, revalidate, remove. */

import { addKey, distinctAccounts, removeKey, revalidateKey } from "../api/keyring.ts";
import { keys$ } from "../state/store.ts";
import type { ApiKeyEntry, KeyHealth } from "../state/types.ts";
import { append, card, clear, el, on } from "./dom.ts";
import { relativeTime } from "../util/format.ts";

const HEALTH_STYLE: Record<KeyHealth, { label: string; variant: string }> = {
  valid: { label: "valid", variant: "success" },
  invalid: { label: "invalid", variant: "danger" },
  rateLimited: { label: "cooling", variant: "warning" },
  unknown: { label: "unknown", variant: "secondary" },
};

export function createKeysPanel(): HTMLElement {
  const list = el("div", { class: "d-flex flex-column gap-2 mb-3" });
  const feedback = el("p", { class: "tiny mb-0 mt-2 d-none" });
  const summary = el("span", { class: "badge text-bg-light" });

  const labelInput = el("input", {
    type: "text",
    class: "form-control form-control-sm",
    placeholder: "Label (optional)",
    autocomplete: "off",
  });

  const keyInput = el("input", {
    type: "password",
    class: "form-control form-control-sm key-input",
    placeholder: "Paste your Torn Public API key",
    autocomplete: "off",
    spellcheck: "false",
  });

  const showToggle = el("button", { type: "button", class: "btn btn-sm btn-outline-secondary", text: "Show" });
  on(showToggle, "click", () => {
    const revealed = keyInput.type === "text";
    keyInput.type = revealed ? "password" : "text";
    showToggle.textContent = revealed ? "Show" : "Hide";
  });

  const saveButton = el("button", { type: "button", class: "btn btn-sm btn-primary", text: "Save" });

  const say = (message: string, kind: "ok" | "warn" | "err") => {
    feedback.textContent = message;
    feedback.className = `tiny mb-0 mt-2 text-${kind === "err" ? "danger" : kind === "warn" ? "warning-emphasis" : "success-emphasis"}`;
  };

  const submit = async () => {
    saveButton.disabled = true;
    saveButton.textContent = "Validating…";
    try {
      const result = await addKey(keyInput.value, labelInput.value);
      say(result.message, result.ok ? (result.duplicateAccount ? "warn" : "ok") : "err");
      if (result.ok) {
        keyInput.value = "";
        labelInput.value = "";
      }
    } finally {
      saveButton.disabled = false;
      saveButton.textContent = "Save";
    }
  };

  on(saveButton, "click", () => void submit());
  on(keyInput, "keydown", (event) => {
    if (event.key === "Enter") void submit();
  });

  const form = el(
    "div",
    { class: "d-flex flex-column gap-2" },
    labelInput,
    el("div", { class: "input-group input-group-sm" }, keyInput, showToggle, saveButton),
    feedback,
  );

  const speedTip = el(
    "div",
    { class: "alert alert-warning tiny mt-3 mb-0 py-2 px-3" },
    el("strong", { text: "Speed tip: " }),
    "Torn's 100 calls/min limit is ",
    el("em", { text: "per account" }),
    ", not per key. A second key from your own account only adds failover — to actually poll " +
      "faster you need keys from other players' accounts.",
  );

  const body = el("div", {}, list, form, speedTip);

  const renderKey = (entry: ApiKeyEntry): HTMLElement => {
    const style = HEALTH_STYLE[entry.health];

    const revalidateBtn = el("button", {
      type: "button",
      class: "btn btn-sm btn-outline-secondary",
      text: "Recheck",
      title: "Re-validate this key against Torn",
    });
    on(revalidateBtn, "click", () => {
      revalidateBtn.disabled = true;
      revalidateBtn.textContent = "…";
      void revalidateKey(entry.id).finally(() => {
        revalidateBtn.disabled = false;
        revalidateBtn.textContent = "Recheck";
      });
    });

    const removeBtn = el("button", {
      type: "button",
      class: "btn btn-sm btn-outline-danger",
      text: "×",
      "aria-label": `Remove key ${entry.label}`,
    });
    on(removeBtn, "click", () => removeKey(entry.id));

    return el(
      "div",
      { class: "border rounded p-2 d-flex align-items-center gap-2 flex-wrap" },
      el(
        "div",
        { class: "flex-grow-1 min-w-0" },
        el(
          "div",
          { class: "d-flex align-items-center gap-2" },
          el("span", { class: "fw-semibold text-truncate", text: entry.label }),
          el("span", { class: `badge text-bg-${style.variant}`, text: style.label }),
        ),
        el("div", {
          class: "tiny text-body-secondary",
          text: entry.playerId
            ? `${entry.playerName} [${entry.playerId}] · last used ${relativeTime(entry.lastUsedAt)}`
            : "unidentified",
        }),
        entry.lastError ? el("div", { class: "tiny text-danger", text: entry.lastError }) : null,
      ),
      el("div", { class: "d-flex gap-1" }, revalidateBtn, removeBtn),
    );
  };

  keys$.subscribe((entries) => {
    clear(list);

    if (entries.length === 0) {
      append(
        list,
        el("p", {
          class: "tiny text-body-secondary mb-0",
          text: "No keys yet. A Public key is all this needs — it only reads items and market listings.",
        }),
      );
    } else {
      for (const entry of entries) append(list, renderKey(entry));
    }

    const accounts = distinctAccounts(entries);
    summary.textContent =
      entries.length === 0
        ? "no keys"
        : `${entries.length} ${entries.length === 1 ? "key" : "keys"} · ${accounts} ${accounts === 1 ? "acct" : "accts"}`;
  });

  const header = el(
    "div",
    { class: "d-flex align-items-center gap-2" },
    summary,
    el("a", {
      class: "small text-decoration-none",
      href: "https://www.torn.com/preferences.php#tab=api",
      target: "_blank",
      rel: "noopener noreferrer",
      text: "Get a key ↗",
    }),
  );

  return card("API keys", body, header);
}
